import {
    useEffect,
    useState,
} from 'react';
import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Alert,
    Button,
    Code,
    Group,
    Modal,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';

import {DnsZone} from '../types';
import {DelegationBadge} from './DelegationBadge';
import {zoneDeletionBlock} from '../hooks/useZoneDeletionGuard';
import {classifyWriteError} from '../services/writeErrors';
import {
    isWriteErrorRetryable,
    writeErrorCopy,
} from '../writeErrorMessages';
// FNP-1365 — the ask for `route53:DeleteHostedZone`, at the moment it is
// needed. See GrantElevationPrompt for why this is an invitation and not an
// error, and why declining must still leave the operator able to finish.
import {GrantElevationPrompt} from '../../provider-connections/components/GrantElevationPrompt';
import {useGrantElevation} from '../../provider-connections/hooks/useGrantElevation';

// FNP-1191 CRUD half — THE CONFIRMATION, AND THE TWO REFUSALS.
//
// ⚠️ A CONTROLLED Mantine `Modal`, NOT `modals.openConfirmModal`.
//
// The DELETE pattern doc reaches for `modals.openConfirmModal`, and that is the
// right call in the apps it was written from. It is NOT available here:
// @mantine/modals is a dependency but NO `ModalsProvider` is mounted in
// src/App.tsx, so `modals.openConfirmModal` would silently do nothing — the
// worst possible failure for a destructive-action guard, because the delete
// button would appear inert rather than broken. Mounting a provider in App.tsx
// is an application-shell change and this ticket is a module ticket.
//
// The doc's SUBSTANCE is kept in full: confirm before deleting, name what is
// being deleted, red confirm action, clear selection after. Only the mechanism
// differs, and it differs because the doc's mechanism does not work in this app.
//
// ⚠️ THIS MODAL NAMES THE ZONE AND SHOWS ITS DELEGATION. A confirmation that
// says "delete 1 item?" is a rubber stamp — the operator already knows they
// clicked delete. What they may NOT know is that this particular zone is live,
// which is the fact that should stop them.

export const DeleteZoneModal = (
    {
        zone,
        opened,
        onClose,
        onConfirm,
        isDeleting = false,
        error,
    }: {
        zone?: DnsZone,
        opened: boolean,
        onClose: () => void,
        onConfirm: () => void,
        isDeleting?: boolean,
        // The rejected mutation, unclassified. Classified here so the modal can
        // render the refusal INLINE — a toast is gone before an operator has
        // finished reading it, and ZONE_NOT_EMPTY is a message they must act on.
        error?: unknown,
    },
) => {

    // The typed-name confirmation for a live zone. Reset whenever the modal
    // opens or the zone changes, or a previous confirmation would carry over
    // and pre-arm the button for a DIFFERENT zone.
    const [typedName, setTypedName] = useState('');

    useEffect(() => {
        setTypedName('');
    }, [opened, zone?.id]);

    // FNP-1365 — asked BEFORE the attempt, from the stored declaration, so this
    // is an offer rather than an apology for a failure. Hooks run
    // unconditionally; the early return below is what guards the render.
    const elevation = useGrantElevation(zone?.connectionId, 'deleteZones');

    if (!zone) {
        return null;
    }

    const block = zoneDeletionBlock(zone);
    const writeError = error ? classifyWriteError(error) : undefined;

    // ⚠️ A HARD BLOCK ARMS ONLY ON AN EXACT NAME MATCH.
    //
    // Deleting a live zone is not something to reach by clicking through a
    // dialog with the same gesture that opened it. Typing the zone name is the
    // friction — it forces the operator to READ which zone this is, which is
    // the failure this modal exists to prevent (deleting the wrong zone from a
    // list of similar names).
    const hardBlocked = block?.overridable === false;
    const nameConfirmed = typedName.trim() === zone.name;

    // FNP-1365 — ⚠️ THE GRANT IS A HARDER BLOCK THAN THE TYPED NAME.
    //
    // Without `route53:DeleteHostedZone` the request cannot succeed whatever
    // the operator types, so the confirm stays disabled while the permission is
    // missing — including after a decline, where the way forward is the Route 53
    // console rather than this button. Offering a click that is certain to be
    // refused at the provider is the failure the elevation prompt replaces.
    const permissionMissing = !elevation.granted
        && (elevation.shouldPrompt || elevation.declined);

    const confirmDisabled = isDeleting
        || permissionMissing
        || (hardBlocked && !nameConfirmed);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={I18n.get('Delete zone')}
            size={'lg'}
            centered={true}
        >
            <Stack gap={'md'}>

                {/* NAMES WHAT IS BEING DELETED — the pattern doc's requirement,
                    and the reason this is not a generic "1 item" dialog. */}
                <Stack gap={4}>
                    <Text size={'sm'}>{I18n.get('You are about to delete this zone:')}</Text>
                    <Group gap={'xs'}>
                        <Code>{zone.name}</Code>
                        <DelegationBadge zone={zone}/>
                    </Group>
                    <Text size={'xs'} c={'dimmed'}>
                        {I18n.get('Connection')}: <Code>{zone.connectionId}</Code>
                    </Text>
                </Stack>

                {/* ─── FNP-1365: THE PERMISSION THEY WITHHELD ───

                    ⚠️ FIRST, ABOVE EVERY OTHER GUARD, because it is the reason
                    this delete cannot proceed at all. A delegation warning
                    below an unusable confirm button asks the operator to weigh
                    a decision they cannot act on yet.

                    ⚠️ AND SHOWN, NOT HIDDEN. The zone toolbar hides the Delete
                    button when the connection cannot delete — correct for a
                    toolbar, but it means a tenant who withheld this grant never
                    learns the feature exists. Reaching the modal by any other
                    route (a row action, a deep link) lands here instead. */}
                {elevation.shouldPrompt && elevation.currentSelection && (
                    <GrantElevationPrompt
                        grant={'deleteZones'}
                        currentSelection={elevation.currentSelection}
                        onGrant={elevation.grantIt}
                        onDecline={elevation.decline}
                        isSaving={elevation.isSaving}
                        // Names THIS zone, because a generic "delete it in AWS"
                        // leaves them hunting through a console for which one.
                        manualAlternative={`Delete the hosted zone ${zone.name} in the Route 53 console, then remove it here.`}
                    />
                )}

                {/* ⚠️ THE DECLINE PATH, AND IT IS NOT A DEAD END. The ticket
                    forbids a wall: an operator who said no must still be able
                    to finish the job. They cannot delete THROUGH Flexi, so what
                    they get is the way to do it themselves — stated plainly,
                    every time, rather than the prompt they dismissed. */}
                {elevation.declined && !elevation.granted && (
                    <Alert
                        color={'gray'}
                        variant={'light'}
                        title={I18n.get('Delete this zone in AWS')}
                        data-testid={'elevation-declined-zone'}
                    >
                        <Text size={'sm'}>
                            {I18n.get('Flexi does not have permission to delete hosted zones for you, and will not ask again. Delete this zone in the Route 53 console and it will stop appearing here.')}
                        </Text>
                    </Alert>
                )}

                {/* ─── GUARD 1: DELEGATION (checked before the attempt) ─── */}
                {block && (
                    <Alert
                        color={block.overridable ? 'yellow' : 'red'}
                        variant={'light'}
                        title={block.title}
                    >
                        <Stack gap={'xs'}>
                            <Text size={'sm'}>{block.explanation}</Text>
                            {hardBlocked && (
                                <TextInput
                                    label={I18n.get('Type the zone name to confirm')}
                                    description={I18n.get('This zone is answering live DNS. Typing its name confirms you mean this one.')}
                                    placeholder={zone.name}
                                    value={typedName}
                                    onChange={event => setTypedName(event.currentTarget.value)}
                                />
                            )}
                        </Stack>
                    </Alert>
                )}

                {/* ─── GUARD 2: THE PROVIDER'S REFUSAL (after the attempt) ───
                    ZONE_NOT_EMPTY above all. Rendered here rather than only as
                    a toast, because it names a concrete next step and the
                    operator is still looking at this dialog. */}
                {writeError && (
                    <Alert
                        color={'red'}
                        variant={'light'}
                        title={writeErrorCopy(writeError.kind).title}
                    >
                        <Stack gap={'xs'}>
                            <Text size={'sm'}>
                                {writeErrorCopy(writeError.kind).action}
                            </Text>
                            {/* The provider's own words, VERBATIM. */}
                            {writeError.providerMessage && (
                                <Text size={'xs'} c={'dimmed'}>
                                    {writeError.providerMessage}
                                </Text>
                            )}
                            <Code>{writeError.kind}</Code>
                        </Stack>
                    </Alert>
                )}

                <Text size={'sm'}>
                    {productCopy('This cannot be undone. The zone and its records are removed at the provider, and {product} does not keep a copy to restore from.')}
                </Text>

                <Group justify={'flex-end'}>
                    <Button variant={'default'} onClick={onClose} disabled={isDeleting}>
                        {I18n.get('Cancel')}
                    </Button>
                    <Button
                        color={'red'}
                        onClick={onConfirm}
                        loading={isDeleting}
                        disabled={confirmDisabled}
                    >
                        {/* Retryable failures say so; ZONE_NOT_EMPTY does NOT
                            offer a retry, because clicking again cannot succeed
                            until the records are gone. */}
                        {writeError && isWriteErrorRetryable(writeError.kind)
                            ? I18n.get('Try again')
                            : I18n.get('Delete zone')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
